import React from "react";

import {
  isProcedure,
  Procedure,
  ProcedureDetail,
  ProcValidation,
  SmartApplyValidationResult,
  Status,
  StatusValue,
} from "../../../../application/domain/proc-management";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import type { GrafferPermissions } from "../../../../application/domain/rbac/rbac";
import { useAuthContextV2 } from "../../../../auth-state";
import { useModal } from "../../../../utils/custom-hooks";
import { PORTAL_BASE_URL } from "../../../../utils/url-utils";
import { Title } from "../../../atoms/proc-management/title";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import {
  FormNotice,
  EditorControl,
  StatusControl,
  anyAction,
} from "../../../molecules/proc-management/form";
import { GroupShareModal } from "../../common/group-share-modal";
import { ProcedureForm } from "./procedure-form";
import apiClient from "../../../../application/apis/api-client";
import { reportProcManagementRelatedError } from "../../../../application/domain/proc-management/util/error";
import { envConfig, isLGWANURL } from "@graffer-inc/smart-apply";
import { ProcedureDetailsGroupPermissionAttached } from "../../../../application/domain/proc-management/core/proc-management-procedure-details";
import { Auth } from "../../../../application/domain/auth/auth";
import { WarningBanner } from "@graffer-inc/console-design-kit";

const statusPermMap: Record<StatusValue, GrafferPermissions> = {
  [Status.Draft]: "proc_management.procedures.update_draft",
  [Status.Published]: "proc_management.procedures.update_published",
  [Status.Archived]: "proc_management.procedures.update_archived",
} as const;

type Props = {
  organizationSlug?: string;
  procedure: ProcedureDetail | Procedure;
  smartApply?: Pick<ProcedureEntity, "id" | "publishStatus" | "orgId">;
  onPressSave: () => void;
  onChange: (path: (string | number)[], value: any) => void;
  onFileAttached: (
    path: (string | number)[],
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  isUpdating: boolean;
  getFileUploading: (path: (string | number)[]) => boolean;
  publish: () => Promise<void>;
  unPublish: () => Promise<void>;
  archive: () => Promise<void>;
  copy: (name: string) => Promise<string | undefined>;
  publishProcAndSmartApply: () => Promise<void>;
  isServiceProviderAccount?: boolean;
  canEditGroupId: boolean;
  onAddGroup: (groupId: string) => void;
  disabled?: boolean;
  shouldDisplayError?: boolean;
  validationErrors: ProcValidation;
  smartApplyValidationResult: SmartApplyValidationResult;
};

export const Content: React.FC<Props> = ({
  organizationSlug,
  procedure,
  smartApply,
  onPressSave,
  onChange,
  isUpdating,
  onFileAttached,
  getFileUploading,
  publish,
  unPublish,
  archive,
  copy,
  publishProcAndSmartApply,
  isServiceProviderAccount = false,
  canEditGroupId,
  onAddGroup,
  disabled = false,
  shouldDisplayError = false,
  validationErrors,
  smartApplyValidationResult,
}) => {
  const { isActive, open, close } = useModal();
  const { auth } = useAuthContextV2();
  const showConfigGroupButton =
    (canEditGroupId && !isProcedure(procedure)) ||
    (canEditGroupId &&
      isProcedure(procedure) &&
      auth.hasPermissions("proc_management.procedures.manage_groups"));

  const disableForm = disabled || isDisabledForm(auth, procedure);
  const slug = organizationSlug ? organizationSlug : auth.data?.orgSlug;
  const publishUrl = isProcedure(procedure)
    ? `${PORTAL_BASE_URL}/${slug}/proc-search/procedures/${procedure.id}`
    : "";
  const fetchPreviewUrl = async () => {
    const host = isLGWANURL(window.location.href)
      ? envConfig.getLgwanHost()
      : PORTAL_BASE_URL!;
    try {
      if (isProcedure(procedure)) {
        const { data } =
          await apiClient.procManagement.generateProcedurePreviewToken(
            procedure.id,
            procedure.jurisdiction.organizationId,
          );
        return `${host}/${slug}/proc-search/procedures/${procedure.id}?test_key=${data.previewToken}`;
      }
    } catch (e) {
      reportProcManagementRelatedError(e);
    }
    return publishUrl;
  };

  return (
    <>
      <div className="columns">
        <div className="column">
          <Title
            text={title(procedure)}
            withStatus={
              isProcedure(procedure) ? procedure.contentStatus : undefined
            }
          />
        </div>
        {!disabled &&
          showStatusControl(procedure) &&
          anyAction(auth, procedure.contentStatus) && (
            <div className="column is-narrow">
              <StatusControl
                orgId={procedure.jurisdiction.organizationId}
                procedure={procedure}
                procName={procedure.name}
                smartApply={smartApply}
                isServiceProviderAccount={isServiceProviderAccount}
                currentStatus={procedure.contentStatus}
                publishUrl={publishUrl}
                publish={publish}
                unPublish={unPublish}
                archive={archive}
                copy={copy}
                publishProcAndSmartApply={publishProcAndSmartApply}
                fetchPreviewUrl={fetchPreviewUrl}
              />
            </div>
          )}
      </div>
      {!disabled && (
        <>
          <FormNotice isServiceProviderAccount={isServiceProviderAccount} />
          <hr className="my-3" />
          <RBACHidden
            requirePermissions={statusPermMap[procedure.contentStatus]}
          >
            <EditorControl
              onPressSave={onPressSave}
              disablePressSave={isUpdating}
              saveLabel={saveLabel(procedure)}
              showConfigGroupButton={showConfigGroupButton}
              onPressConfigGroup={open}
              configGroupLabel={configGroupLabel(procedure)}
            />
          </RBACHidden>
          {!procedure.jurisdiction.groupId && (
            <div className="mt-3 mb-3">
              <WarningBanner message="管理グループが未設定です。手続きを新規作成する場合には必ず管理グループを１つ設定してください。" />
            </div>
          )}
        </>
      )}
      <ProcedureForm
        procedure={procedure}
        onChange={onChange}
        onFileAttached={onFileAttached}
        getFileUploading={getFileUploading}
        isServiceProviderAccount={isServiceProviderAccount}
        disabled={disableForm}
        shouldDisplayError={shouldDisplayError}
        validationErrors={validationErrors}
        smartApplyValidationResult={smartApplyValidationResult}
      />
      {!disabled && (
        <>
          <RBACHidden
            requirePermissions={statusPermMap[procedure.contentStatus]}
          >
            <EditorControl
              onPressSave={onPressSave}
              disablePressSave={isUpdating}
              saveLabel={saveLabel(procedure)}
              showConfigGroupButton={showConfigGroupButton}
              onPressConfigGroup={open}
              configGroupLabel={configGroupLabel(procedure)}
            />
          </RBACHidden>
        </>
      )}

      {isActive && (
        <GroupShareModal
          onClose={close}
          onAdd={onAddGroup}
          sharedGroupIds={
            procedure.jurisdiction.groupId
              ? [procedure.jurisdiction.groupId]
              : []
          }
        />
      )}
    </>
  );
};

export default Content;

const showStatusControl = (proc: ProcedureDetail | Procedure) => {
  return isProcedure(proc);
};

const title = (proc: ProcedureDetail | Procedure) => {
  return isProcedure(proc)
    ? `手続き編集: ${proc.name ?? ""}`
    : "手続き新規作成";
};

const saveLabel = (proc: ProcedureDetail | Procedure) => {
  return isProcedure(proc) ? "現在の内容を保存する" : "下書きとして保存する";
};

const configGroupLabel = (proc: ProcedureDetail | Procedure) => {
  return typeof proc.jurisdiction.groupId === "string" && isProcedure(proc)
    ? "管理グループを変更する"
    : "管理グループを設定する";
};

const isDisabledForm = (auth: Auth, procedure: ProcedureDetail | Procedure) => {
  const requirePerms = statusPermMap[procedure.contentStatus];
  if (auth.isServiceProviderAccount()) {
    return !auth.hasPermissions(requirePerms);
  }
  return !auth.checkGroupPermission(
    new ProcedureDetailsGroupPermissionAttached(procedure),
    [requirePerms],
  );
};
